import type { Study } from '@bioavail/shared-types';
import {
  CalculationMode as CM,
  StudyType as ST,
  RouteOfAdministration as ROA,
  ArmRole as AR,
  AnalyteType as AT,
  ConcentrationUnit as CU,
  TimeUnit as TU,
  DoseUnit as DU,
  TrapezoidalRule as TR,
  BlqRule as BR,
  AucInterval as AI,
} from '@bioavail/shared-types';

export interface Preset {
  id: string;
  name: string;
  description: string;
  build: () => Pick<Study, 'mode' | 'studyType' | 'aucInterval' | 'arms'>;
}

function makeArm(overrides: Partial<Study['arms'][number]> & { id: string }): Study['arms'][number] {
  return {
    label: 'Untitled arm',
    route: ROA.Oral,
    role: AR.Test,
    dose: 100,
    doseUnit: DU.Mg,
    analyteType: AT.Parent,
    concentrationUnit: CU.NgPerMl,
    timeUnit: TU.Hour,
    dataPoints: [],
    usesDirectAuc: false,
    trapezoidalRule: TR.LinearUpLogDown,
    blqRule: BR.M1,
    lambdaZManualOverride: false,
    ...overrides,
  };
}

export const PRESETS: Preset[] = [
  {
    id: 'iv-vs-oral',
    name: 'IV vs Oral (Absolute F)',
    description: 'IV reference + oral test — absolute bioavailability.',
    build: () => ({
      mode: CM.Absolute,
      studyType: ST.SingleDose,
      aucInterval: AI.ZeroToInf,
      arms: [
        makeArm({
          id: 'arm-test',
          label: 'Test (Oral 200 mg)',
          route: ROA.Oral,
          role: AR.Test,
          dose: 200,
        }),
        makeArm({
          id: 'arm-ref',
          label: 'Reference (IV 100 mg)',
          route: ROA.IV,
          role: AR.Reference,
          dose: 100,
        }),
      ],
    }),
  },
  {
    id: 'tablet-vs-capsule',
    name: 'Tablet vs Capsule (Relative F)',
    description: 'Two oral arms — relative bioavailability.',
    build: () => ({
      mode: CM.Relative,
      studyType: ST.SingleDose,
      aucInterval: AI.ZeroToInf,
      arms: [
        makeArm({ id: 'arm-test', label: 'Test (Tablet 50 mg)', dose: 50 }),
        makeArm({
          id: 'arm-ref',
          label: 'Reference (Capsule 50 mg)',
          role: AR.Reference,
          dose: 50,
        }),
      ],
    }),
  },
  {
    id: 'test-vs-reference',
    name: 'Test vs Reference (Relative F)',
    description: 'Generic test vs branded reference — relative bioavailability.',
    build: () => ({
      mode: CM.Relative,
      studyType: ST.SingleDose,
      aucInterval: AI.ZeroToInf,
      arms: [
        makeArm({ id: 'arm-test', label: 'Test (Generic 100 mg)', dose: 100 }),
        makeArm({
          id: 'arm-ref',
          label: 'Reference (Branded 100 mg)',
          role: AR.Reference,
          dose: 100,
        }),
      ],
    }),
  },
];

export function getPreset(id: string): Preset | undefined {
  return PRESETS.find((p) => p.id === id);
}
